<script setup lang="ts">
import BaseInputSelect from '../../../base/BaseInputSelect.vue'
import { computed, defineEmits, defineProps, reactive, ref, RenderFunction, watch } from 'vue'
import { AccountDataState, KycDocumentVerificationType, SelectOption } from '../../../../typings'
import { FormContext, useForm } from 'vee-validate'
import { MinimalKycDocument } from '../../../../utilities/api'
import { CreditCardIcon, UserIcon } from '@heroicons/vue/solid'
import BaseInputText from '../../../base/BaseInputText.vue'
import BaseButtonMuted from '../../../base/BaseButtonMuted.vue'
import BaseKycDocumentFileUpload from '../../../base/BaseKycDocumentFileUpload.vue'

const props = defineProps<{
    verificationType: KycDocumentVerificationType
    store: AccountDataState
    icon: RenderFunction
    modelValue: MinimalKycDocument
    form: FormContext | null
    uploading: boolean
    identificationTypes: SelectOption[]
}>()

const form = useForm({
    validationSchema: {
        'newKycDocument.type': 'required',
        'newKycDocument.kyc_document': 'required|requiredArray',
        'newKycDocument.bankAccount.holder':
            props.verificationType === KycDocumentVerificationType.KycBank ? 'required' : undefined, // only fpr kyc_bank
        'newKycDocument.bankAccount.iban':
            props.verificationType === KycDocumentVerificationType.KycBank ? 'required|iban' : undefined, // only fpr kyc_bank
        // 'newKycDocument.bankAccount.bic': 'required',
    },
})
const emit = defineEmits(['update:modelValue', 'update:form', 'update:uploading'])
emit('update:form', form)

const uploading = ref(props.uploading)
watch(uploading, () => emit('update:uploading', uploading.value))

const newKycDocument = reactive<MinimalKycDocument>(props.modelValue)
watch(newKycDocument, () => emit('update:modelValue', newKycDocument))

function updateStorageKey(files) {
    newKycDocument.storage_key = files.length === 0 ? null : files[0]?.key
}

function updateStorageKeyBack(files) {
    newKycDocument.storage_key_back = files.length === 0 ? null : files[0]?.key
}

const hasProfileBankData = computed(
    () => props.store.getBankDataFromProfile.holder || props.store.getBankDataFromProfile.iban
)

const profileBankDataisDifferent = computed(
    () =>
        props.store.getBankDataFromProfile.iban &&
        (props.store.getBankDataFromProfile.holder !== newKycDocument.bankAccount.holder ||
            props.store.getBankDataFromProfile.iban !== newKycDocument.bankAccount.iban)
)

function setBankDataFromProfile() {
    const bankData = props.store.getBankDataFromProfile
    newKycDocument.bankAccount.holder = bankData.holder
    newKycDocument.bankAccount.iban = bankData.iban
}
</script>

<template>
    <div>
        <div v-if="newKycDocument.bankAccount" class="grid grid-cols-1 gap-x-4 gap-y-2 pb-2 sm:grid-cols-2">
            <div class="sm:col-span-1">
                <BaseInputText
                    v-model="newKycDocument.bankAccount.holder"
                    :icon="UserIcon"
                    name="newKycDocument.bankAccount.holder"
                    :label="$t('verification_kyc_document.account_holder')" />
            </div>
            <div class="sm:col-span-1">
                <BaseInputText
                    v-model="newKycDocument.bankAccount.iban"
                    :icon="CreditCardIcon"
                    name="newKycDocument.bankAccount.iban"
                    :label="$t('account.iban')" />
            </div>

            <div v-if="hasProfileBankData && profileBankDataisDifferent" class="sm:col-span-2">
                <BaseButtonMuted @click="setBankDataFromProfile">{{
                    $t('bank_data.set_bank_data_from_profile')
                }}</BaseButtonMuted>
            </div>

            <!--            Currently not used-->
            <!--            <div class="sm:col-span-1">-->
            <!--                <BaseInputText-->
            <!--                    v-model="newKycDocument.bankAccount.bic"-->
            <!--                    :icon="CreditCardIcon"-->
            <!--                    name="newKycDocument.bankAccount.bic"-->
            <!--                    :label="$t('account.bic')" />-->
            <!--            </div>-->
        </div>
        <BaseInputSelect
            v-model="newKycDocument.type"
            :disabled="identificationTypes.length === 1"
            name="newKycDocument.type"
            rules="required"
            :label="$t('verification_kyc_document.identification_type')"
            :icon="icon"
            :options="identificationTypes">
        </BaseInputSelect>
        <template v-if="props.verificationType === KycDocumentVerificationType.KycId">
            <h4 class="mb-2 mt-6 text-base font-medium leading-6 text-gray-900">{{ $t('id_front') }}</h4>
            <BaseKycDocumentFileUpload
                v-model:uploading="uploading"
                name="newKycDocument.kyc_document"
                class="my-2"
                :camera="true"
                max-file-size="5MB"
                @update:modelValue="updateStorageKey" />

            <h4 class="mb-2 mt-6 text-base font-medium leading-6 text-gray-900">{{ $t('id_back') }}</h4>
            <BaseKycDocumentFileUpload
                v-model:uploading="uploading"
                name="newKycDocument.kyc_document_back"
                class="my-2"
                :camera="true"
                max-file-size="5MB"
                @update:modelValue="updateStorageKeyBack" />
        </template>
        <template v-else>
            <BaseKycDocumentFileUpload
                v-model:uploading="uploading"
                name="newKycDocument.kyc_document"
                class="my-2"
                :camera="true"
                max-file-size="5MB"
                @update:modelValue="updateStorageKey" />
        </template>
    </div>
</template>
